import React from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { Launch } from "@gaming/xds-react-js-icons";

import { IdXboxBadge } from "../../../../components/IdXboxBadge";
import { PartnerAccountIconImage } from "../../../../components/PartnerAccountIconImage";
import {
  partnerCenterDashboardUrl,
  partnerSectionLinks,
  xboxOnboardSiteLocationPrefix,
} from "../../../../constants";
import { useStrings } from "../../../../localization/localizationHelpers";
import { PartnerCardMenu } from "../PartnerCardMenu";
import * as styles from "../../PartnerHomePage.module.css";

type PartnerStudioHeaderProps = Readonly<{
  studioName: string | undefined;
  sellerId: string | undefined;
  legalEntityName?: string;
  addressLines?: string[];
  studioPhone?: string;
  isIdXboxApproved: boolean;
  isEnrollmentComplete: boolean;
  isEnrollmentEarlyComplete: boolean;
  showAgreementsOption?: boolean;
  showDevReadinessGuideOption: boolean;
  showSettingsOption: boolean;
  settingsOnlyMenu?: boolean;
  onShowDevReadinessGuide: () => void;
}>;

export const PartnerStudioHeader = ({
  studioName,
  sellerId,
  legalEntityName,
  addressLines = [],
  studioPhone,
  isIdXboxApproved,
  isEnrollmentComplete,
  isEnrollmentEarlyComplete,
  showAgreementsOption = true,
  showDevReadinessGuideOption,
  showSettingsOption,
  settingsOnlyMenu = false,
  onShowDevReadinessGuide,
}: PartnerStudioHeaderProps) => {
  const [
    sellerIdLabel,
    partnerCenterDevLinkLabel,
    partnerCenterLinkStatus,
    partnerCenterLinkDestination,
    legalEntityNameLabel,
    addressLabel,
    phoneLabel,
  ] = useStrings(
    "sellerIdLabel",
    "partnerHomePartnerCenterDevLinkLabel",
    "partnerHomePartnerCenterLinkStatus",
    "partnerHomePartnerCenterLinkDestination",
    "partnerOverviewLegalEntityNameLabel",
    "address",
    "phone"
  );
  const hasExtendedDetails = legalEntityName || addressLines.length > 0 || studioPhone;

  return (
    <>
      <img
        className={styles.cardBackgroundImage}
        src={`${xboxOnboardSiteLocationPrefix}/account-image`}
        alt=""
        onError={event => {
          event.currentTarget.hidden = true;
        }}
      />
      <div className={styles.cardBanner}>
        <div className={styles.studioImage}>
          <PartnerAccountIconImage />
        </div>
      </div>
      <div className={`${styles.studioRow} ${styles.studioRowBottom}`}>
        <div className={styles.studioDetails}>
          <div className={styles.studioTitleRow}>
            <h2 className={styles.studioName}>{studioName}</h2>
            {isIdXboxApproved && <IdXboxBadge />}
          </div>
          {sellerId && (
            <p className={styles.sellerIdValue}>
              <span>{sellerIdLabel}</span>
              {sellerId}
            </p>
          )}
          {hasExtendedDetails && (
            <dl className={styles.studioMetadata}>
              {legalEntityName && (
                <div>
                  <dt>{legalEntityNameLabel}</dt>
                  <dd>{legalEntityName}</dd>
                </div>
              )}
              {addressLines.length > 0 && (
                <div>
                  <dt>{addressLabel}</dt>
                  <dd>
                    <address>
                      {addressLines.map((line, index) => (
                        <span key={`${line}-${index}`}>{line}</span>
                      ))}
                    </address>
                  </dd>
                </div>
              )}
              {studioPhone && (
                <div>
                  <dt>{phoneLabel}</dt>
                  <dd>{studioPhone}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
        <div className={styles.cardActions}>
          {isEnrollmentComplete && (
            <a
              className={`${styles.partnerCenterLink} ${
                isEnrollmentEarlyComplete ? styles.partnerCenterDevLink : ""
              }`}
              href={partnerCenterDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {isEnrollmentEarlyComplete ? (
                partnerCenterDevLinkLabel
              ) : (
                <>
                  <span className={styles.partnerCenterLinkStatus}>{partnerCenterLinkStatus}</span>
                  <span>{partnerCenterLinkDestination}</span>
                </>
              )}
              <Icon
                aria-hidden="true"
                className={styles.partnerCenterLinkIcon}
                icon={Launch}
                iconSize={IconSize.Custom}
              />
            </a>
          )}
          {(!settingsOnlyMenu || showSettingsOption) && (
            <PartnerCardMenu
              settingsOnly={settingsOnlyMenu}
              managePath={partnerSectionLinks.overview.to}
              showAgreementsOption={showAgreementsOption}
              showDevReadinessGuideOption={showDevReadinessGuideOption}
              showSettingsOption={showSettingsOption}
              onShowDevReadinessGuide={onShowDevReadinessGuide}
            />
          )}
        </div>
      </div>
    </>
  );
};
